import React from 'react'
import { cn } from 'src/sites/kits/Utils'
import { Button, Checkbox, Input, Label, Spacer, Text, Textarea, View } from 'oio-react'
import { SortableElement, SortableHandle } from 'react-sortable-hoc'
import CloseIcon from 'assets/icons/close'
import DragHandleIcon from 'assets/icons/dragHandle'
import FormElementOptionsContainer from './OptionContainer'
import style from './style.less'

// =======================================================
// Element Data
// =======================================================

const elementData = {
   text: {
      title: 'Single line text input',
      labelPlaceholder: 'Enter Text Field Question...',
      descriptionPlaceholder: 'Enter an optional description for this question...'
   },
   multiline: {
      title: 'Paragraph text input',
      labelPlaceholder: 'Enter Text Field Question...',
      descriptionPlaceholder: 'Enter an optional description for this question...'
   },
   info: {
      title: 'Text Explanation or Information',
      description: 'Please enter an explanation or information that provides additional context for this form',
      fillable: false
   },
   radio: {
      title: 'Multiple Choice Form Field',
      labelPlaceholder: 'Enter Multiple Choice Question...',
      descriptionPlaceholder: 'Enter an optional description for this question...',
      description: 'The person filling out the form will only be able to select one of the above options'
   },
   checkbox: {
      title: 'Checkbox Form Field',
      labelPlaceholder: 'Enter Checkbox Question...',
      descriptionPlaceholder: 'Enter an optional description for this question...',
      description: 'The person filling out the form will be able to select multiple options'
   },
   file: {
      title: 'File Upload Field',
      description: 'The person filling out the form will be able to upload a file'
   },
   dropdown: {
      title: 'Drop-down Form Field',
      labelPlaceholder: 'Enter Drop-down menu Question...',
      descriptionPlaceholder: 'Enter an optional description for this question...',
      description: 'The person filling out the form will only be able to select one of the above options'
   }
}

// =======================================================
// Drag Handle
// =======================================================

const FormBuilderCardDragHandle = SortableHandle(() => (
   <View style={{ cursor: 'move' }}>
      <DragHandleIcon width="15px" height="15px" color="#ff6900" strokeWidth="2px" />
   </View>
))

// =======================================================
// Form Builder Card - Sortable Item
// =======================================================

const FormBuilderCard = SortableElement(({
   element,
   elementIndex,
   onInputChange,
   onOptionAdd,
   onOptionChange,
   onOptionRemove,
   onOptionSortEnd,
   onRequiredChange,
   onPropChange,
   onRemove,
   setAllowedFileTypes
}) => {
   const {
      title,
      labelPlaceholder,
      descriptionPlaceholder,
      description,
      fillable = true
   } = elementData[element.multiline ? 'multiline' : element.type]

   // Only applies to file upload elements
   const allowedFileTypes = element.formats ?? ['*/*']

   return (
      <div
         className={cn(
            'float-left block w-full mb-6 rounded-lg border border-solid border-neutral-300 bg-white shadow-sm overflow-hidden',
            style.formBuilderCard
         )}
         style={{ zIndex: 10002 }}>
         <div className="flex justify-between items-center w-full h-10 px-3 bg-neutral-100 rounded-t-lg overflow-hidden">
            <div className="text-neutral-500 text-xs font-medium">{title}</div>
            <View display="flex" alignItems="center">
               <FormBuilderCardDragHandle />
               <Spacer size="2" orientation="vertical" />
               <button type="button" className="cursor-pointer" onClick={() => onRemove && onRemove(elementIndex)}>
                  <CloseIcon width="15px" height="15px" color="#ff6900" strokeWidth="2px" />
               </button>
            </View>
         </div>
         <View position="relative" width="100%" padding="12px 12px 12px 60px">
            <View
               display="flex"
               alignItems="center"
               justifyContent="center"
               position="absolute"
               top="18px"
               left="18px"
               width="24px"
               height="24px"
               backgroundColor="#111"
               borderRadius="50%">
               <Text size="0.9" color="#fff" weight="medium">
                  {elementIndex + 1}
               </Text>
            </View>
            <div className="mt-2">
               {element.type !== 'info' && (
                  <>
                     <Input
                        label="Field Name"
                        type="text"
                        onChange={e => onInputChange && onInputChange(elementIndex, 'label', e)}
                        placeholder={labelPlaceholder}
                        value={element.label}
                        required
                     />
                     <Spacer size="2" />
                  </>
               )}
               <Textarea
                  label="Field Description"
                  onChange={e => onInputChange && onInputChange(elementIndex, 'description', e)}
                  placeholder={descriptionPlaceholder}
                  value={element.description}
               />
            </div>
            {['checkbox', 'radio', 'dropdown'].includes(element.type) && (
               <>
                  <Spacer size="2" />
                  <Label>Options</Label>
                  {element.options.length > 0 && (
                     <FormElementOptionsContainer
                        element={element}
                        elementIndex={elementIndex}
                        onOptionChange={onOptionChange}
                        onOptionRemove={onOptionRemove}
                        onOptionSortEnd={onOptionSortEnd}
                        onSortEnd={onOptionSortEnd}
                        useDragHandle
                     />
                  )}
                  <View padding="6px 0px 18px 42px">
                     <Button
                        onClick={() => onOptionAdd(elementIndex)}
                        size="xs"
                        name="Add Option"
                        color="rgba(250, 147, 104, 0.2)"
                        textColor="#ff6900"
                        padding="12px"
                     />
                  </View>
               </>
            )}
            {element.type === 'file' && (
               <>
                  <Spacer size="2" />
                  <Label>Allowed File Types</Label>
                  <Checkbox
                     checked={allowedFileTypes.length === 1 && allowedFileTypes[0] === '*/*'}
                     highlightColor="#ff6900"
                     label="All "
                     onChange={(e) => {
                        setAllowedFileTypes(elementIndex, '*/*', e.currentTarget.checked)
                     }}
                  />
                  <Checkbox
                     checked={allowedFileTypes.includes('image/*')}
                     highlightColor="#ff6900"
                     label="Images "
                     onChange={e => setAllowedFileTypes(elementIndex, 'image/*', e.currentTarget.checked)}
                  />
                  <Checkbox
                     checked={allowedFileTypes.includes('application/pdf')}
                     highlightColor="#ff6900"
                     label="PDFs"
                     onChange={e => setAllowedFileTypes(elementIndex, 'application/pdf', e.currentTarget.checked)}
                  />
                  <View padding="6px 0px 18px 42px" />
               </>
            )}
            {description && (
               <Text size="1.5" color="#888" className={style.singleLineText}>
                  {description}
               </Text>
            )}
            {element.type === 'file' && (
               <View width="100%" paddingTop="6px">
                  <Checkbox
                     onChange={e => onPropChange?.(elementIndex, 'allowMultiple', e.currentTarget.checked)}
                     defaultChecked={element.allowMultiple ?? false}
                     value="true"
                     label="Allow multiple file uploads"
                     highlightColor="#ff6900"
                  />
               </View>
            )}
            {fillable && (
               <div className="flex items-center gap-2 mt-2 text-xs text-neutral-500">
                  <div className="w-6">
                     <Checkbox
                        onChange={e => onRequiredChange && onRequiredChange(elementIndex, e)}
                        defaultChecked={element.required}
                        value="true"
                        highlightColor="#ff6900"
                     />
                  </div>
                  This field is required
               </div>
            )}
         </View>
      </div>
   )
})

export default FormBuilderCard
